import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { platformClient } from "@/lib/musicApi";

// Round 2 winback banner. Former subscribers only. Copy lives in
// home.winback.* across all ten locales. CTA is chosen once per browser
// session (stable within a page view, varied across sessions). The headline
// uses i18next plural machinery for each count separately so every locale
// carries the forms it needs (Arabic 6, Russian 4, Chinese 1). Zero-count
// handling: name only the non-zero noun; the both-zero case is suppressed
// server-side (show=false) so the banner never pitches "0 videos".

// Stable within a session; index persisted so it doesn't reshuffle per render.
function pickCtaIndex(len) {
  try {
    const saved = sessionStorage.getItem("winback_cta_idx");
    if (saved !== null) return Number(saved) % len;
    const n = Math.floor(Math.random() * len);
    sessionStorage.setItem("winback_cta_idx", String(n));
    return n;
  } catch {
    return 0;
  }
}

export const WinbackBanner = () => {
  const { subscriptionState } = usePlatformAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (subscriptionState !== "former") return;
    platformClient.get("/home/winback").then((r) => setData(r.data)).catch(() => {});
  }, [subscriptionState]);

  // Nothing paints until the endpoint answers -> no flash of a wrong/countless
  // line that later corrects itself.
  if (subscriptionState !== "former" || !data?.show) return null;

  const ctas = t("home.winback.ctas", { returnObjects: true });
  const ctaList = Array.isArray(ctas) ? ctas : [];
  const cta = ctaList[pickCtaIndex(ctaList.length || 1)] || "";

  let headline;
  if (!data.has_counts) {
    headline = t("home.winback.headline_countless");
  } else {
    const tracks = data.new_tracks > 0 ? t("home.winback.count_tracks", { count: data.new_tracks }) : null;
    const videos = data.new_videos > 0 ? t("home.winback.count_videos", { count: data.new_videos }) : null;
    if (tracks && videos) headline = t("home.winback.headline_both", { tracks, videos });
    else if (tracks) headline = t("home.winback.headline_tracks", { tracks });
    else headline = t("home.winback.headline_videos", { videos });
  }

  return (
    <section data-testid="winback-banner" className="relative py-8" style={{ background: "var(--accent-light)" }}>
      <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p
          className="text-lg sm:text-xl font-bold text-center sm:text-left"
          data-testid="winback-headline"
          style={{ color: "var(--text-primary)" }}
        >
          {headline}
        </p>
        <button
          onClick={() => navigate("/pricing")}
          data-testid="winback-cta"
          className="btn-pill btn-primary inline-flex items-center gap-2 whitespace-nowrap"
        >
          {cta}
        </button>
      </div>
    </section>
  );
};

export default WinbackBanner;
